import { _ } from "../../polyglot/polyglot.js";
import {
	companyNormalRoleName,
	getPresentationName,
} from "../../../models/CompanyUserRoles.js";
import { guid } from "../../helpers/string-util.js";
import { select } from "../../elements/index.js";
import confirmationDialog from "../confirmationDialog.js";
import MultipleItemPickerWidget from "../MultipleItemPickerWidget/MultipleItemPickerWidget.js";
import SearchFieldWidget from "../SearchFieldWidget/SearchFieldWidget.js";

/**
 * `attachCompanyDialog` lets the user selects a company to attach
 * to a target.
 *
 * @param {string} spec.target - Name of the object to attach to
 * @param {company[]} spec.companies - Companies that can be
 * attached to the edited user
 * @param {boolean} spec.displayRoles - Display the roles
 * selection dropdown when true
 * @param {companyRole[]} spec.roles - Available company roles
 */
export default confirmationDialog.subclass((that, my) => {
	my.initialize = function ({ target, companies, displayRoles, roles }) {
		my.super(...arguments);

		my.target = target;
		my.companies = companies;
		my.displayRoles = displayRoles;
		my.roles = roles;
		my.selectedRole = companyNormalRoleName;

		my.cssClass = "attach-company resizable-modal-content";
	};

	my.initializeSubwidgets = function () {
		my.super(...arguments);

		const companyItems = my.companies.map((company) => ({
			value: company,
		}));

		my.searchFieldWidget = new SearchFieldWidget({
			placeholder: _("Search…"),
			inputId: guid(),
		});

		my.multipleCompanySelection = new MultipleItemPickerWidget({
			title: _("Companies"),
			subtitle: _(
				"Selected: {numberOfSelectedCompanies} out of {totalNumberOfCompanies} companies",
				{
					numberOfSelectedCompanies: (html) =>
						html.render(
							my.multipleCompanySelection.selectedValues().length,
						),
					totalNumberOfCompanies: companyItems.length,
				},
			),
			items: companyItems,
			itemRenderer: (item) => item.value.getName(),
			itemVisibilityMatcher: (searchString, item) =>
				item.value.match(searchString),
			allowCollapsing: false,
		});

		my.searchFieldWidget.applySearchOn(my.multipleCompanySelection);

		if (my.displayRoles) {
			my.roleDropdown = select({
				selected: my.selectedRole,
				items: my.roles.map((role) => ({
					label: getPresentationName(role),
					value: role,
				})),
				onChange: (role) => (my.selectedRole = role),
			});
		}

		my.title = _("Attach a company to {name}", {
			name: my.target,
		});

		my.content = function (html) {
			html.div(
				{ klass: "clearfix" },
				html.div(
					{ klass: "search-field-container" },
					html.div(
						{ klass: "col-md-6 clearfix" },
						my.searchFieldWidget,
					),
				),
				html.div({ klass: "col-sm-12" }, my.multipleCompanySelection),
				my.fieldRenderer(_("Role"), my.roleDropdown),
			);
		};
	};

	my.fieldRenderer = function (name, widget) {
		return function (html) {
			if (!widget) {
				return;
			}

			html.div(
				html.div({ klass: "col-sm-4" }, name),
				html.div({ klass: "col-sm-8" }, widget),
			);
		};
	};

	my.triggerConfirm = function () {
		const companies = my.multipleCompanySelection.selectedValues();
		const role = my.roleDropdown ? my.selectedRole : undefined;
		that.onConfirm.trigger(companies, role);
	};
});
